import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import UniversalLayout from "./components/UniversalLayout";
import MdxRenderer from "./components/blog/MdxRenderer";
import { DOCS_PAGE } from "./content/docsPages";
import "./DocsPage.css";

const HEADING_ID_MAP = {
  "quick-start-workflows": "quick-start-workflows",
  "composer-ai-video-generator": "composer-ai-video-generator",
  "projects-dashboard": "projects-dashboard",
  "editor-timeline": "editor-timeline",
  "ugc-ads": "ugc-ads",
  "studio-talking-head-videos": "studio-talking-head",
  "academic-paper-videos": "academic-paper-videos",
  "brain-explorer": "brain-explorer",
  "assets-and-characters": "assets-and-characters",
  "automations": "automations",
  "billing-and-account": "billing-and-account",
  "export-share-and-support": "export-share-support",
  "troubleshooting": "troubleshooting",
  "frequently-asked-questions": "frequently-asked-questions",
};

function slugifyText(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function HeadingRenderer(level) {
  const Tag = `h${level}`;

  return function DocsHeading({ children, id, ...rest }) {
    const generatedId = id || HEADING_ID_MAP[slugifyText(children)] || slugifyText(children);
    return (
      <Tag id={generatedId} {...rest}>
        {children}
      </Tag>
    );
  };
}

const DOCS_MDX_COMPONENTS = {
  h2: HeadingRenderer(2),
  h3: HeadingRenderer(3),
};

function formatUpdatedDate(dateString) {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function DocsPage() {
  const [source, setSource] = useState("");
  const [loadError, setLoadError] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setLoadError("");

    async function loadMdxSource() {
      try {
        const response = await fetch(DOCS_PAGE.mdxPublicPath, {
          headers: {
            Accept: "text/plain",
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load documentation source: ${response.status}`);
        }

        const mdxText = await response.text();
        if (!cancelled) {
          setSource(mdxText);
          setIsLoading(false);
        }
      } catch (error) {
        console.error("[DocsPage] Failed to load docs:", error);
        if (!cancelled) {
          setLoadError(
            "The documentation is temporarily unavailable. Please refresh and try again."
          );
          setIsLoading(false);
        }
      }
    }

    loadMdxSource();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <UniversalLayout>
      <main className="docs-page">
        <div className="docs-page__inner">
          <nav className="docs-page__breadcrumb" aria-label="Breadcrumb">
            <ol>
              <li><Link to="/">Home</Link></li>
              <li aria-current="page">Documentation</li>
            </ol>
          </nav>

          <header className="docs-page__hero">
            <p className="docs-page__eyebrow">Documentation</p>
            <h1 className="docs-page__title">{DOCS_PAGE.title}</h1>
            <p className="docs-page__description">{DOCS_PAGE.description}</p>
            <div className="docs-page__meta-row" aria-label="Documentation metadata">
              <span>Updated {formatUpdatedDate(DOCS_PAGE.updatedAt)}</span>
              <span>{DOCS_PAGE.readingTimeMinutes} min read</span>
              <span>Creation, editing, automation, billing</span>
            </div>
          </header>

          <section
            className="docs-page__quick-start"
            aria-labelledby="docs-quick-start-heading"
          >
            <p className="docs-page__section-label">Start here</p>
            <h2 id="docs-quick-start-heading" className="docs-page__section-title">
              Choose the path that matches your job
            </h2>
            <div className="docs-page__quick-grid">
              {DOCS_PAGE.quickStartCards.map((card) => (
                <a key={card.title} href={card.href} className="docs-page__quick-card">
                  <h3>{card.title}</h3>
                  <p>{card.description}</p>
                  <span className="docs-page__quick-arrow" aria-hidden="true">Read section →</span>
                </a>
              ))}
            </div>
          </section>

          <div className="docs-page__layout">
            <article className="docs-page__article" itemScope itemType="https://schema.org/TechArticle">
              {isLoading ? (
                <div className="docs-page__status" aria-live="polite">
                  <div className="docs-page__skeleton" />
                  <div className="docs-page__skeleton docs-page__skeleton--short" />
                  <div className="docs-page__skeleton" />
                  <div className="docs-page__skeleton docs-page__skeleton--short" />
                </div>
              ) : loadError ? (
                <p className="docs-page__status docs-page__status--error">
                  {loadError}
                </p>
              ) : (
                <div itemProp="articleBody">
                <MdxRenderer
                  source={source}
                  className="docs-page__content"
                  components={DOCS_MDX_COMPONENTS}
                />
                </div>
              )}
            </article>

            <aside className="docs-page__toc" aria-label="Documentation sections">
              <p className="docs-page__toc-title">On this page</p>
              <ul className="docs-page__toc-list">
                {DOCS_PAGE.sections.map((section) => (
                  <li key={section.id}>
                    <a className="docs-page__toc-link" href={`#${section.id}`}>
                      {section.label}
                    </a>
                  </li>
                ))}
              </ul>
              <Link className="docs-page__toc-cta" to="/pricing">Review pricing →</Link>
            </aside>
          </div>

          <section className="docs-page__cta" aria-labelledby="docs-cta-heading">
            <div>
              <h2 id="docs-cta-heading">Start a Latted project with the workflow you just learned.</h2>
              <p>Use Composer for a broad brief, or open a specialized workflow when the format is already clear.</p>
            </div>
            <div className="docs-page__cta-actions">
              <Link to="/" className="docs-page__button">Create a video</Link>
              <Link to="/examples" className="docs-page__button docs-page__button--secondary">View examples</Link>
            </div>
          </section>
        </div>
      </main>
    </UniversalLayout>
  );
}

export default DocsPage;
